import React from "react";
import axios from "axios";
import { AlertColor, Button, IconButton, InputAdornment, TextField, useMediaQuery} from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import { DataGridPro, GridColumns, GridRowParams, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { CustomTablePagination } from "../../component/CustomPagination";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { OpenMode } from "../../../common/Enums";
import { Manufacturer } from "./ManufacturerAccessories";
import { defaultPaymentTerm } from "../../../common/Constants";
import { ManufacturerSingle, ManufacturerSingleHandle } from "./ManufacturerSingle";

export function ManufacturerList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<ManufacturerSingleHandle>(null);

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

	//Grid columns
	const columns: GridColumns = [
		{ field: 'name', headerName: 'Name', flex: 4, type: 'string' },
		{ field: 'manufacturerCode', headerName: 'Code', flex: 1, type: 'string' }
	];

    const [pageSize, setPageSize] = React.useState(15);

    const [searchText, setSearchText] = React.useState("");

	const [gridData, setGridData] = React.useState<Manufacturer[]>([]);

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		LoadData(searchText);
    }

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData(text: string) {
		setLoading(true);

		let searchText = text == '' ? 'ANY' : text;
		const params = { searchString: searchText };
		axios.get<Manufacturer[]>('Api/V1/Manufacturer/Search', {
			params,
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				setGridData(result.data);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}


	function CustomToolbar() {
		const [inputText, setInputText] = React.useState(searchText);
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<TextField className={style.ModalContentFieldHalfWidth}
					style={{ minWidth: '150px', width: '100%' }}
					label="Manufacturer name"
					size="small"
					value={inputText}
					onChange={(e) => setInputText(e.target.value)}
					onKeyDown={(e) => KeyPressed(e.key, inputText)}
					InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<IconButton onClick={() => {
									setSearchText(inputText);
									LoadData(inputText);
								}}>
								<Search />
							</IconButton>
						</InputAdornment>
					),
					}}            
				/>
				<Button variant="contained" color="secondary" style={{ margin: "2px" }} startIcon={<Add />} onClick={() => NewObject()}>New</Button>
			</GridToolbarContainer>
		);
	}

    function KeyPressed(key: string, inputText: string) {
		if(key === 'Enter'){
		  setSearchText(inputText)
		  LoadData(inputText);
		}      
	}
  
	function NewObject() {
		let maxId = Math.max.apply(Math, gridData.map(function(o) { return o.manufacturerID; }));
		let manufacturer: Manufacturer = {
			manufacturerID: maxId+1,
			manufacturerCode: (maxId+1).toString().padStart(3, '0'),
			name: "",
			paymentTerm: defaultPaymentTerm
		}
		singleModalRef.current?.Initialise(OpenMode.NEW, manufacturer);
				setLoading(false);
	}

	function EditObject(params: GridRowParams<any>) {
        setLoading(true);
		let manufacturerId = params.row.manufacturerID;
		axios.get<Manufacturer>('Api/V1/Manufacturer/' + manufacturerId.toString(), {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				singleModalRef.current?.Initialise(OpenMode.EDIT, result.data);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Manufacturer List" />
			<div className={style.PageContent}>
				<DataGridPro
					getRowId={(row) => row.manufacturerID}
					rows={gridData}
					columns={columns}
					pageSize={pageSize}
					onPageSizeChange={(pageSize) => setPageSize(pageSize)}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
					onRowDoubleClick={EditObject}
				/>
			</div>

			<ManufacturerSingle
				ref={singleModalRef}
				paymentTerms={props.paymentTerms}
				onClose={(saved: boolean, manufacturer: Manufacturer) => {
					LoadData(searchText);
				}}
			/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

